/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { joiResolver } from "@hookform/resolvers/joi";
import * as Joi from "joi";
import { isEqual } from "lodash";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Alert, Modal, Spinner } from "react-bootstrap";
import { CookiesProvider, useCookies } from "react-cookie";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BiArrowBack } from "react-icons/bi";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "../../app/hook";
import {
  useUpdateAiWidgetAppearanceMutation,
  useUpdateMcBotWidgetAppearanceMutation
} from "../../app/services/chatbot-rtk.services";
import LoadingComponent from "../../components/common/loading/loading.component";
import LouiseButton from "../../components/ui/LouiseButton";
import { ChatbotEditContext } from "../../pages/dashboard/ChatbotEdit";
import { BotTypeEnum } from "../../utils/BotTypeEnum";
import { BotCode } from "../bot/types";
import AppearanceHumanClone from "./AppearanceHumanClone";
import AppearancePreview from "./AppearancePreview";
import AppearanceSharedAi from "./AppearanceSharedAi";
import {
  handleResetAppearance,
  selectAppearanceState
} from "./appearanceSlice";
import AppearanceTextScenario from "./AppearanceTextScenario";
import AppearanceVideoScenario from "./AppearanceVideoScenario";
import { AppearanceType, IAiAppearance } from "./types";

const BotAppearanceEdit = () => {
  const { t } = useTranslation();
  const ChatbotEditContextValue = useContext(ChatbotEditContext);
  const scrollElRef = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (ChatbotEditContextValue?.value) {
      setIsLoading(ChatbotEditContextValue?.value.isLoading);
    } else {
      setIsLoading(true);
    }
  }, [ChatbotEditContextValue?.value]);

  const appearanceState = useAppSelector(selectAppearanceState);

  const [cookies, setCookie, removeCookie] = useCookies([
    `Louise_Widget_${appearanceState.id}`
  ]);

  // Mutators
  const [updateAiWidgetAppearance, UpdateAiWidgetAppearanceResults] =
    useUpdateAiWidgetAppearanceMutation();
  const [updateMcBotWidgetAppearance, updateMcBotWidgetAppearanceResults] =
    useUpdateMcBotWidgetAppearanceMutation();

  const [clonedData, setClonedData] = useState<any>();

  const [smallScreenShowLivePreview, setSmallScreenShowLivePreview] =
    useState(false);

  const sharedAiValidationSchema = Joi.object({
    name: Joi.object({
      type: Joi.string().valid("auto", "other", "louisebot").required(),
      value: Joi.string().allow("").required()
    }).required(),
    icon: Joi.object({
      type: Joi.string().valid("icon", "image", "video").required(),
      value: Joi.alternatives().conditional("type", {
        is: "icon",
        then: Joi.string().required(),
        otherwise: Joi.alternatives().try(Joi.string(), Joi.object()).required()
      })
    }).required(),
    position: Joi.string().required(),
    isVisible: Joi.boolean().optional(),
    isVoiceActive: Joi.boolean().required(),
    logo: Joi.alternatives()
      .try(Joi.string().allow(null), Joi.object())
      .required(),
    widget_poweredBy: Joi.boolean().allow(null),
    show_initial_message: Joi.boolean(),
    save_conversation: Joi.boolean(),
    saveConversationEnableTime: Joi.optional().allow(null, ""),
    saveStatsEnableTime: Joi.optional().allow(null, ""),
    theme: Joi.string().required(),
    color: Joi.string().required(),
    poweredBy: Joi.boolean().optional(),
    customPoweredBy: Joi.object({
      companyName: Joi.string().optional().allow(null, ""),
      companyLogo: Joi.string().optional().allow(null, ""),
      companyUrl: Joi.string()
        .uri({ scheme: ["https"] }) // Only allows HTTPS URLs
        .optional()
        .allow(null, "")
        .label(t("AppearanceCustomPoweredBy/key7"))
    }).allow(null),
    isOpen: Joi.boolean().required(),
    voice: Joi.string().optional(),
    voiceResponse: Joi.boolean().allow(null),
    roundedVideo: Joi.boolean().allow(null),
    // workOnDesktop: Joi.boolean().required(),
    alwaysShowButtons: Joi.boolean().allow(true, false, null),
    assistantGender: Joi.string().required(),
    operatingHours: Joi.object({
      isActive: Joi.boolean().required(),
      times: Joi.object({
        sat: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        sun: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        mon: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        tue: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        wed: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        thu: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        fri: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        exc: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required()
      }).required(),
      selectedTimezone: Joi.string().allow(null, "").optional()
    }).required(),
    notificationSound: Joi.string()
      .uri({ scheme: ["http", "https"] })
      .allow(null, ""),
    notificationMessage: Joi.alternatives().conditional(
      "isNotificationMessageActive",
      {
        is: true,
        then: Joi.string()
          .required()
          .label("Message")
          .messages({
            "string.base": t("DashboardSettingCustomizeChat/e-01", {
              name: "Message"
            }), // Custom message for "must be a string"
            "string.empty": t("DashboardSettingCustomizeChat/e-01", {
              name: "Message"
            }), // For empty strings
            "any.required": t("DashboardSettingCustomizeChat/e-01", {
              name: "Message"
            }) // For required validation
          }),
        otherwise: Joi.string()
          .allow(null, "")
          .messages({
            "string.base": t("DashboardSettingCustomizeChat/e-01", {
              name: "Message"
            }), // Custom message for "must be a string"
            "string.empty": t("DashboardSettingCustomizeChat/e-01", {
              name: "Message"
            }), // For empty strings
            "any.required": t("DashboardSettingCustomizeChat/e-01", {
              name: "Message"
            }) // For required validation
          })
      }
    ),
    notificationMessageTextColor: Joi.alternatives().conditional(
      "notificationMessageTextColor",
      {
        is: true,
        then: Joi.string().required().label("Color"),
        otherwise: Joi.string().allow(null, "")
      }
    ),
    notificationBadge: Joi.boolean().required(),
    isNotificationMessageActive: Joi.boolean().required(),
    isNotificationSoundActive: Joi.boolean().required(),
    isFullScreen: Joi.boolean().optional(),
    saveStats: Joi.boolean().optional(),
    sessionLimit: Joi.number().optional()
  });

  const videoScenarioValidationSchema = Joi.object({
    name: Joi.object({
      type: Joi.string().valid("auto", "other", "louisebot").required(),
      value: Joi.string().allow("").required()
    }).required(),
    icon: Joi.object({
      type: Joi.string().valid("icon", "image", "video").required(),
      value: Joi.alternatives().conditional("type", {
        is: "icon",
        then: Joi.string().required(),
        otherwise: Joi.alternatives().try(Joi.string(), Joi.object()).required()
      })
    }).required(),
    position: Joi.string().required(),
    logo: Joi.alternatives()
      .try(Joi.string().allow(null), Joi.object())
      .required(),
    widget_poweredBy: Joi.boolean().allow(null),
    show_initial_message: Joi.boolean(),
    isVisible: Joi.boolean().optional(),
    theme: Joi.string().required(),
    color: Joi.string().required(),
    poweredBy: Joi.boolean().optional(),
    saveStatsEnableTime: Joi.optional().allow(null, ""),
    customPoweredBy: Joi.object({
      companyName: Joi.string().optional().allow(null, ""),
      companyLogo: Joi.string().optional().allow(null, ""),
      companyUrl: Joi.string()
        .uri({ scheme: ["https"] }) // Only allows HTTPS URLs
        .optional()
        .allow(null, "")
        .label(t("AppearanceCustomPoweredBy/key7"))
    }).allow(null),
    isOpen: Joi.boolean().required(),
    roundedVideo: Joi.boolean().allow(null),
    // workOnDesktop: Joi.boolean().required(),
    alwaysShowButtons: Joi.boolean().allow(true, false, null),
    operatingHours: Joi.object({
      isActive: Joi.boolean().required(),
      times: Joi.object({
        sat: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        sun: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        mon: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        tue: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        wed: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        thu: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        fri: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        exc: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required()
      }).required(),
      selectedTimezone: Joi.string().allow(null, "").optional()
    }).required(),
    notificationSound: Joi.string()
      .uri({ scheme: ["http", "https"] })
      .allow(null, ""),
    notificationMessage: Joi.alternatives().conditional(
      "isNotificationMessageActive",
      {
        is: true,
        then: Joi.string()
          .required()
          .label("Message")
          .messages({
            "string.base": t("DashboardSettingCustomizeChat/e-01", {
              name: "Message"
            }), // Custom message for "must be a string"
            "string.empty": t("DashboardSettingCustomizeChat/e-01", {
              name: "Message"
            }), // For empty strings
            "any.required": t("DashboardSettingCustomizeChat/e-01", {
              name: "Message"
            }) // For required validation
          }),
        otherwise: Joi.string()
          .allow(null, "")
          .messages({
            "string.base": t("DashboardSettingCustomizeChat/e-01", {
              name: "Message"
            }), // Custom message for "must be a string"
            "string.empty": t("DashboardSettingCustomizeChat/e-01", {
              name: "Message"
            }), // For empty strings
            "any.required": t("DashboardSettingCustomizeChat/e-01", {
              name: "Message"
            }) // For required validation
          })
      }
    ),
    notificationMessageTextColor: Joi.alternatives().conditional(
      "notificationMessageTextColor",
      {
        is: true,
        then: Joi.string().required().label("Color"),
        otherwise: Joi.string().allow(null, "")
      }
    ),
    notificationBadge: Joi.boolean().required(),
    isNotificationMessageActive: Joi.boolean().required(),
    isNotificationSoundActive: Joi.boolean().required(),
    isFullScreen: Joi.boolean().optional(),
    saveStats: Joi.boolean().optional(),
    voice: Joi.string().optional(),
    sessionLimit: Joi.number().optional()
  });

  const textScenarioValidationSchema = Joi.object({
    name: Joi.object({
      type: Joi.string().valid("auto", "other", "louisebot").required(),
      value: Joi.string().allow("").required()
    }).required(),
    icon: Joi.object({
      type: Joi.string().valid("icon", "image", "video").required(),
      value: Joi.alternatives().conditional("type", {
        is: "icon",
        then: Joi.string().required(),
        otherwise: Joi.alternatives().try(Joi.string(), Joi.object()).required()
      })
    }).required(),
    position: Joi.string().required(),
    logo: Joi.alternatives()
      .try(Joi.string().allow(null), Joi.object())
      .required(),
    widget_poweredBy: Joi.boolean().allow(null),
    isVisible: Joi.boolean().optional(),
    show_initial_message: Joi.boolean(),
    theme: Joi.string().required(),
    color: Joi.string().required(),
    poweredBy: Joi.boolean().optional(),
    saveStatsEnableTime: Joi.optional().allow(null, ""),
    customPoweredBy: Joi.object({
      companyName: Joi.string().optional().allow(null, ""),
      companyLogo: Joi.string().optional().allow(null, ""),
      companyUrl: Joi.string()
        .uri({ scheme: ["https"] }) // Only allows HTTPS URLs
        .optional()
        .allow(null, "")
        .label(t("AppearanceCustomPoweredBy/key7"))
    }).allow(null),
    isOpen: Joi.boolean().required(),
    roundedVideo: Joi.boolean().allow(null),
    // workOnDesktop: Joi.boolean().required(),
    alwaysShowButtons: Joi.boolean().allow(true, false, null),
    operatingHours: Joi.object({
      isActive: Joi.boolean().required(),
      times: Joi.object({
        sat: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        sun: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        mon: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        tue: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        wed: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        thu: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        fri: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        exc: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required()
      }).required(),
      selectedTimezone: Joi.string().allow(null, "").optional()
    }).required(),
    notificationSound: Joi.string()
      .uri({ scheme: ["http", "https"] })
      .allow(null, ""),
    notificationMessage: Joi.alternatives().conditional(
      "isNotificationMessageActive",
      {
        is: true,
        then: Joi.string()
          .required()
          .label("Message")
          .messages({
            "string.base": t("DashboardSettingCustomizeChat/e-01", {
              name: "Message"
            }), // Custom message for "must be a string"
            "string.empty": t("DashboardSettingCustomizeChat/e-01", {
              name: "Message"
            }), // For empty strings
            "any.required": t("DashboardSettingCustomizeChat/e-01", {
              name: "Message"
            }) // For required validation
          }),
        otherwise: Joi.string()
          .allow(null, "")
          .messages({
            "string.base": t("DashboardSettingCustomizeChat/e-01", {
              name: "Message"
            }), // Custom message for "must be a string"
            "string.empty": t("DashboardSettingCustomizeChat/e-01", {
              name: "Message"
            }), // For empty strings
            "any.required": t("DashboardSettingCustomizeChat/e-01", {
              name: "Message"
            }) // For required validation
          })
      }
    ),
    notificationMessageTextColor: Joi.alternatives().conditional(
      "notificationMessageTextColor",
      {
        is: true,
        then: Joi.string().required().label("Color"),
        otherwise: Joi.string().allow(null, "")
      }
    ),
    notificationBadge: Joi.boolean().required(),
    isNotificationMessageActive: Joi.boolean().required(),
    isNotificationSoundActive: Joi.boolean().required(),
    isFullScreen: Joi.boolean().optional(),
    saveStats: Joi.boolean().optional(),
    voice: Joi.string().optional(),
    sessionLimit: Joi.number().optional()
  });

  const humanCloneValidationSchema = Joi.object({
    name: Joi.object({
      type: Joi.string().valid("auto", "other", "louisebot").required(),
      value: Joi.string().allow("").required()
    }).required(),
    logo: Joi.alternatives()
      .try(Joi.string().allow(null), Joi.object())
      .required(),
    isVoiceActive: Joi.boolean(),
    show_initial_message: Joi.boolean(),
    theme: Joi.string().required(),
    color: Joi.string().required(),
    assistantGender: Joi.string().required(),
    poweredBy: Joi.boolean(),
    voiceResponse: Joi.boolean(),
    saveStats: Joi.boolean().optional(),
    saveStatsEnableTime: Joi.optional().allow(null, ""),
    enableSeo: Joi.boolean().optional(),
    operatingHours: Joi.object({
      isActive: Joi.boolean().required(),
      times: Joi.object({
        sat: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        sun: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        mon: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        tue: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        wed: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        thu: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        fri: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required(),
        exc: Joi.array()
          .min(0)
          .allow(
            Joi.object({
              from: Joi.string().required(),
              to: Joi.string().required()
            })
          )
          .required()
      }).required(),
      selectedTimezone: Joi.string().allow(null, "").optional()
    }).required(),
    sessionLimit: Joi.number().optional()
  });

  const validationSchema: { [key in BotCode]: any } = {
    KITTY_02: sharedAiValidationSchema,
    KITTY_03: sharedAiValidationSchema,
    KITTY_04: sharedAiValidationSchema,
    MCBOT_01: textScenarioValidationSchema,
    VIDEO_SCENARIO: videoScenarioValidationSchema,
    CLONE_01: humanCloneValidationSchema
  };

  const currentValidationSchema =
    validationSchema[appearanceState.bot_code as BotCode];

  const methods = useForm({
    shouldUnregister: false,
    resolver: joiResolver(currentValidationSchema),
    mode: "all"
  });

  const {
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    getValues,
    setValue
  } = methods;

  const appearanceComponents = {
    KITTY_02: <AppearanceSharedAi />,
    KITTY_03: <AppearanceSharedAi />,
    KITTY_04: <AppearanceSharedAi />,
    MCBOT_01: <AppearanceTextScenario />,
    VIDEO_SCENARIO: <AppearanceVideoScenario />,
    CLONE_01: <AppearanceHumanClone />
  };

  const handleCancelAppearanceConfig = () => {
    dispatch(handleResetAppearance());

    reset();
  };

  const checkKeyDown = (e: any) => {
    if (e.key === "Enter") e.preventDefault();
  };

  const onSubmit = (data: any) => {
    const clonedData: AppearanceType =
      appearanceState.configs as AppearanceType;

    const formData = new FormData();

    if (
      (clonedData as IAiAppearance).logo &&
      (clonedData as IAiAppearance).logo !==
        "https://cdn.1cdn.app/application/LOUISEBOT/24050807084353_Logo_(2).png" &&
      (clonedData as IAiAppearance).logo !==
        "https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png" &&
      typeof (clonedData as IAiAppearance).logo === "object"
    )
      formData.append("avatar", (clonedData as IAiAppearance).logo);

    if (
      (clonedData as any).icon &&
      typeof (clonedData as any).icon.value === "object" &&
      ((clonedData as IAiAppearance).icon.type === "image" ||
        (clonedData as IAiAppearance).icon.type === "video")
    )
      formData.append("icon", (clonedData as IAiAppearance).icon.value);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { voice, ...rest } = clonedData as IAiAppearance;

    formData.append(
      "settings",
      JSON.stringify({
        ...{
          ...(appearanceState.bot_code !== BotTypeEnum.VIDEO_SCENARIO
            ? clonedData
            : rest)
        },
        icon:
          appearanceState.bot_code !== BotTypeEnum.HUMAN_CLONE
            ? (clonedData as IAiAppearance).icon
            : undefined,
        logo:
          (clonedData as IAiAppearance).logo !==
            "https://cdn.1cdn.app/application/LOUISEBOT/24050807084353_Logo_(2).png" &&
          (clonedData as IAiAppearance).logo !==
            "https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png" &&
          typeof (clonedData as IAiAppearance).logo === "object"
            ? ""
            : (clonedData as IAiAppearance).logo
      })
    );

    if (
      appearanceState.bot_code !== BotTypeEnum.TEXT_SCENARIO &&
      appearanceState.bot_code !== BotTypeEnum.VIDEO_SCENARIO
    )
      updateAiWidgetAppearance({ id: appearanceState.id, data: formData })
        .unwrap()
        .then(() => {
          toast.success("Successfully updated");

          removeCookie(`Louise_Widget_${appearanceState.id}`, { path: "/" });

          //handleCancelAppearanceConfig();

          setClonedData(appearanceState);
        })
        .catch((err) => {
          if (err.status < 500) toast.warn(err?.data?.message || err.message);

          if (err.status >= 500) toast.error(err?.data?.message || err.message);
        });
    else
      updateMcBotWidgetAppearance({
        id: appearanceState.id,
        data: formData
      })
        .unwrap()
        .then(() => {
          toast.success("Successfully updated");

          removeCookie(`Louise_Widget_${appearanceState.id}`, { path: "/" });

          //handleCancelAppearanceConfig();
          setClonedData(appearanceState);
        })
        .catch((err) => {
          if (err.status < 500) toast.warn(err?.data?.message || err.message);

          if (err.status >= 500) toast.error(err?.data?.message || err.message);
        });
  };

  const renderErrors = () => {
    const errorsArray = Object.values(errors);

    if (errorsArray.length > 0) {
      const error: any = errorsArray[0];

      let errorMessage = "";

      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.value?.message) {
        errorMessage = error.value.message;
      }

      return errorMessage;
    }
  };

  useEffect(() => {
    reset({
      ...appearanceState.configs
      // poweredBy: appearanceState.configs?.poweredBy
      //   ? !appearanceState.configs?.poweredBy
      //   : true
    });
  }, [appearanceState.showModal]);

  // Preventing api calls when nothing is changed => (Mocking isDirty)
  useEffect(() => {
    if (appearanceState.bot_code) {
      setClonedData(appearanceState);
    }

    setValue("poweredBy", !appearanceState.configs?.poweredBy);

    return () => {
      setClonedData(undefined);
    };
  }, [appearanceState.bot_code]);

  if (isLoading) {
    return <LoadingComponent />;
  }

  return (
    <CookiesProvider>
      <div className="tw-w-full tw-bg-white tw-rounded-2xl tw-shadow tw-flex-col tw-justify-start tw-items-end tw-gap-6 tw-inline-flex tw-mb-4">
        <FormProvider {...methods}>
          {/* Create alert for errors generated by react hook form */}
          <form
            className="tw-w-full"
            onSubmit={handleSubmit(onSubmit)}
            onKeyDown={(e) => checkKeyDown(e)}
          >
            <div
              className="tw-grid tw-grid-cols-12 tw-h-full "
              ref={scrollElRef}
            >
              <div className="2xl:tw-col-span-9 xl:tw-col-span-8 md:tw-col-span-6 tw-col-span-12  tw-p-6 tw-h-full">
                <div className="lg:tw-px-6 tw-px-2 lg:tw-pt-6 tw-pt-2 tw-pb-8  tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-flex">
                  {appearanceComponents[appearanceState.bot_code as BotCode]}
                </div>
              </div>
              <div className="2xl:tw-col-span-3 xl:tw-col-span-4 md:tw-col-span-6  tw-p-6 md:tw-inline-flex tw-hidden">
                <AppearancePreview key={1} />
              </div>
            </div>
            <div
              className={` ${
                errors && Object.keys(errors).length > 0
                  ? "tw-justify-between"
                  : "md:tw-justify-end tw-justify-between"
              } tw-gap-3 tw-px-6 tw-pb-6`}
            >
              <hr className="tw-bg-gray-200" />
              {errors && renderErrors() && Object.keys(errors).length > 0 && (
                <Alert
                  variant="danger"
                  className="tw-p-2 tw-flex tw-flex-row tw-items-center tw-flex-wrap tw-gap-1 tw-text-sm tw-mr-auto"
                >
                  <Alert.Heading className="tw-text-sm tw-font-semibold tw-mb-0">
                    You have the following validation errors:
                  </Alert.Heading>
                  - {renderErrors()}
                </Alert>
              )}

              <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-3">
                <span className="tw-text-gray-800 text-xs lg:tw-inline-flex tw-block">
                  {t("DashboardSettingCustomizeChat/updateHint")}
                </span>

                <div className="tw-flex tw-items-center tw-gap-2 tw-ml-auto">
                  <LouiseButton
                    variant="lightBlue"
                    type="button"
                    className="tw-rounded-full md:tw-hidden"
                    onClick={() => setSmallScreenShowLivePreview(true)}
                  >
                    {t("DashboardSettingCustomizeChat/preview")}
                  </LouiseButton>

                  <LouiseButton
                    variant="primary"
                    type="submit"
                    className="tw-rounded-full"
                    disabled={
                      UpdateAiWidgetAppearanceResults.isLoading ||
                      updateMcBotWidgetAppearanceResults?.isLoading ||
                      isEqual(clonedData?.configs, appearanceState?.configs)
                    }
                  >
                    {(UpdateAiWidgetAppearanceResults?.isLoading ||
                      updateMcBotWidgetAppearanceResults?.isLoading) && (
                      <Spinner size="sm" className="tw-mr-2" />
                    )}
                    {t("DashboardSettingCustomizeChat/apply")}
                  </LouiseButton>
                </div>
              </div>
            </div>
          </form>
        </FormProvider>
      </div>

      {/* Live preview for small screens */}
      <Modal
        show={smallScreenShowLivePreview}
        onHide={() => setSmallScreenShowLivePreview(false)}
        centered
        fullscreen
      >
        <Modal.Header className="tw-justify-center tw-relative tw-bg-gray-200">
          <div className="tw-whitespace-nowrap tw-text-gray-700 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
            {t("DashboardSettingCustomizeChat/preview")}
          </div>
          <button
            className="tw-border-0 tw-bg-transparent tw-absolute tw-top-2 tw-left-2"
            onClick={() => setSmallScreenShowLivePreview(false)}
          >
            <BiArrowBack size={22} className="tw-text-gray-400" />
          </button>
        </Modal.Header>
        <Modal.Body className="tw-bg-gray-200">
          <AppearancePreview key={2} />
        </Modal.Body>
      </Modal>
    </CookiesProvider>
  );
};

export default BotAppearanceEdit;
